import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, BadgeCheck, ChevronLeft, ChevronRight, Loader2, MapPin, Phone, Share2, Tag, X } from "lucide-react";
import { toast } from "sonner";
import { api, mediaUrl } from "@/lib/api";import { useSeo } from "@/hooks/useSeo";
import { CardVisual } from "@/components/CardVisual";
import { BusinessMap } from "@/components/BusinessMap";
import { AffiliateBanner } from "@/components/AffiliateBanner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useHref, useT } from "@/i18n";
import { useCategoryLabel } from "@/i18n/categories";

export default function BusinessDetail() {
  const { listingId } = useParams();
  const href = useHref();
  const t = useT();
  const catLabel = useCategoryLabel();
  const [listing, setListing] = useState(null);
  const [related, setRelated] = useState([]);
  const [error, setError] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const touchX = useRef(null);

  const photos = listing?.photos || [];

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowRight") setLightbox((i) => (i + 1) % photos.length);
      if (e.key === "ArrowLeft") setLightbox((i) => (i - 1 + photos.length) % photos.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, photos.length]);

  useEffect(() => {
    setListing(null);
    setError(false);
    api
      .get(`/listings/${listingId}`)
      .then(({ data }) => {
        setListing(data);
        return api.get("/listings", { params: { category: data.category, state: data.state, limit: 4 } });
      })
      .then((res) => res && setRelated(res.data.items.filter((l) => l.id !== listingId).slice(0, 3)))
      .catch(() => setError(true));
  }, [listingId]);

  const jsonLd = useMemo(() => {
    if (!listing) return null;
    return {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      name: listing.business_name,
      telephone: listing.phone_number,
      description: listing.description || `${catLabel(listing.category)}, ${listing.district}, ${listing.state}`,
      address: {
        "@type": "PostalAddress",
        streetAddress: listing.street_address || undefined,
        postalCode: listing.postcode || undefined,
        addressLocality: listing.district,
        addressRegion: listing.state,
        addressCountry: "MY",
      },
      keywords: (listing.search_tags || []).join(", "),
    };
  }, [listing]);

  useSeo({
    title: listing
      ? t("detail.seo.title", {
          name: listing.business_name,
          category: catLabel(listing.category),
          district: listing.district,
          state: listing.state,
        })
      : t("detail.seo.fallbackTitle"),
    description: listing
      ? t("detail.seo.desc", {
          name: listing.business_name,
          category: catLabel(listing.category),
          district: listing.district,
          state: listing.state,
          phone: listing.phone_number,
        })
      : undefined,
    canonical: `${window.location.origin}${href(`/business/${listing?.slug || listingId}`)}`,
    image: `${process.env.REACT_APP_BACKEND_URL}/api/listings/${listing?.slug || listingId}/og.png`,
    jsonLd,
  });

  if (error) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-24 text-center sm:px-8">
        <h1 data-testid="business-not-found" className="font-display text-2xl font-bold tracking-tight">
          {t("detail.notFound.title")}
        </h1>
        <p className="mt-3 text-sm text-slate-600">{t("detail.notFound.body")}</p>
        <Link to={href("/directory")} className="mt-8 inline-block">
          <Button data-testid="back-to-directory-button" className="bg-primary hover:bg-[#0052CC]">
            {t("detail.backToDir")}
          </Button>
        </Link>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="grid h-80 place-items-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  const img = null;
  const extraPhoto = photos[0] || null;
  const galleryPhotos = photos.slice(1);

  // The share link goes through the backend so WhatsApp / Facebook bots (which do not run JS)
  // can read the per-listing preview card before redirecting the visitor to this page.
  const shareUrl = `${process.env.REACT_APP_BACKEND_URL}/api/share/${listingId}`;

  const share = async () => {
    // WhatsApp only renders a preview when the message is just the link.
    if (navigator.share) {
      try {
        await navigator.share({ title: listing.business_name, url: shareUrl });
        return;
      } catch {
        return; // user dismissed the sheet
      }
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success(t("detail.shareCopied"));
      return;
    } catch {
      // Clipboard API is blocked in some in-app browsers — fall through.
    }
    const field = document.createElement("textarea");
    field.value = shareUrl;
    field.style.position = "fixed";
    field.style.opacity = "0";
    document.body.appendChild(field);
    field.select();
    const copied = document.execCommand("copy");
    field.remove();
    if (copied) {
      toast.success(t("detail.shareCopied"));
      return;
    }
    window.open(`https://wa.me/?text=${encodeURIComponent(shareUrl)}`, "_blank", "noopener");
  };

  return (
    <div>
      <div className="mx-auto max-w-5xl px-4 py-10 sm:px-8">
        <Link
          to={href("/directory")}
          data-testid="detail-back-link"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 transition-colors duration-200 hover:text-primary"
        >
          <ArrowLeft className="h-4 w-4" /> {t("detail.back")}
        </Link>

        <article data-testid="business-detail" className="mt-6 grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="aspect-[16/10] overflow-hidden rounded-lg border border-border bg-slate-100">
              {img ? (
                <img src={img} alt={t("card.cardAlt", { name: listing.business_name })} className="h-full w-full object-cover" />
              ) : (
                <CardVisual listing={listing} />
              )}
            </div>
            {extraPhoto && (
              <button
                type="button"
                onClick={() => setLightbox(0)}
                data-testid="detail-extra-photo"
                className="mt-4 block w-full aspect-[16/10] overflow-hidden rounded-lg border border-border bg-slate-100"
              >
                <img
                  src={mediaUrl(extraPhoto)}
                  alt={t("detail.photoAlt", { name: listing.business_name, n: 1 })}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-300 hover:scale-105"
                />
              </button>
            )}
          </div>

          <div className="lg:col-span-7">
            <Badge variant="secondary" className="text-[11px] font-semibold uppercase tracking-wider">
              {catLabel(listing.category)}
            </Badge>
            {listing.is_featured && (
              <Badge data-testid="detail-featured-badge" className="ml-2 bg-amber-100 text-[11px] font-semibold uppercase tracking-wider text-amber-900">
                Featured
              </Badge>
            )}
            <h1
              data-testid="detail-business-name"
              className="mt-4 font-display text-3xl font-extrabold leading-none tracking-tight sm:text-4xl"
            >
              {listing.business_name}
            </h1>
            <p className="mt-4 flex flex-wrap items-center gap-1.5 text-base text-slate-600">
              <MapPin className="h-4 w-4 shrink-0" />
              {listing.district}, {listing.state}
              {listing.verified && (
                <span
                  data-testid="detail-verified-badge"
                  className="ml-1 inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-800 ring-1 ring-emerald-200"
                >
                  <BadgeCheck className="h-3.5 w-3.5" /> {t("card.verified")}
                </span>
              )}
            </p>
            {listing.street_address && (
              <p data-testid="detail-street-address" className="mt-2 text-base leading-relaxed text-slate-700">
                {listing.street_address}
                {listing.postcode ? `, ${listing.postcode}` : ""}
              </p>
            )}
            {listing.description && (
              <p className="mt-4 max-w-xl text-base leading-relaxed text-slate-700">{listing.description}</p>
            )}

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <a
                href={`tel:${listing.phone_number.replace(/\s|-/g, "")}`}
                data-testid="detail-call-button"
                className="inline-flex items-center justify-center gap-2 rounded-md border border-border px-5 py-3 text-sm font-semibold text-slate-800 transition-colors duration-200 hover:bg-slate-900 hover:text-white"
              >
                <Phone className="h-4 w-4" /> {listing.phone_number}
              </a>
              <a
                href={listing.whatsapp_link}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="detail-whatsapp-button"
                className="inline-flex items-center justify-center gap-2 rounded-md bg-[#25D366] px-5 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#1DA851]"
              >
                {t("card.whatsapp")}
              </a>
              <button
                type="button"
                data-testid="detail-share-button"
                onClick={share}
                className="inline-flex items-center justify-center gap-2 rounded-md border border-border px-5 py-3 text-sm font-semibold text-slate-800 transition-colors duration-200 hover:bg-slate-900 hover:text-white"
              >
                <Share2 className="h-4 w-4" /> {t("detail.share")}
              </button>
            </div>

            {listing.claimable && (
              <div
                data-testid="claim-listing-panel"
                className="mt-8 rounded-lg border border-sky-200 bg-sky-50 p-5"
              >
                <p className="font-display text-base font-bold tracking-tight text-sky-950">
                  {t("detail.claim.title")}
                </p>
                <p className="mt-1.5 text-sm leading-relaxed text-sky-900">
                  {t("detail.claim.body")}
                </p>
                <Link to={href(`/claim-card/${listingId}`)} className="mt-4 inline-block">
                  <Button
                    data-testid="claim-this-listing-button"
                    className="bg-sky-900 text-white hover:bg-sky-800"
                  >
                    <BadgeCheck className="mr-2 h-4 w-4" /> {t("detail.claim.cta")}
                  </Button>
                </Link>
              </div>
            )}

            {(listing.search_tags || []).length > 0 && (
              <div className="mt-10" data-testid="detail-tags">
                <p className="flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-slate-500">
                  <Tag className="h-3.5 w-3.5" /> {t("detail.tags")}
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {listing.search_tags.slice(0, 18).map((tag) => (
                    <Link key={tag} to={`${href("/directory")}?q=${encodeURIComponent(tag)}`}>
                      <Badge
                        variant="outline"
                        className="text-[11px] transition-colors duration-200 hover:border-primary hover:text-primary"
                      >
                        {tag}
                      </Badge>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>

        <BusinessMap listing={listing} />

        {galleryPhotos.length > 0 && (
          <section className="mt-16" data-testid="photo-gallery">
            <h2 className="font-display text-xl font-bold tracking-tight sm:text-2xl">{t("detail.gallery")}</h2>
            <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3">
              {galleryPhotos.map((p, i) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setLightbox(i + 1)}
                  data-testid={`gallery-photo-${i}`}
                  className="aspect-[4/3] overflow-hidden rounded-lg border border-border bg-slate-100"
                >
                  <img
                    src={mediaUrl(p)}
                    alt={t("detail.photoAlt", { name: listing.business_name, n: i + 1 })}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-300 hover:scale-105"
                  />
                </button>
              ))}
            </div>
          </section>
        )}

        {lightbox !== null && photos[lightbox] && (
          <div
            data-testid="gallery-lightbox"
            onClick={() => setLightbox(null)}
            className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-950/95 p-4 backdrop-blur-sm"
            onTouchStart={(e) => {
              touchX.current = e.touches[0].clientX;
            }}
            onTouchEnd={(e) => {
              if (touchX.current === null) return;
              const dx = e.changedTouches[0].clientX - touchX.current;
              touchX.current = null;
              if (Math.abs(dx) < 45 || photos.length < 2) return;
              setLightbox((i) => (i + (dx < 0 ? 1 : -1) + photos.length) % photos.length);
            }}
          >
            <button
              type="button"
              data-testid="lightbox-close"
              onClick={() => setLightbox(null)}
              className="absolute right-4 top-4 rounded-full bg-white/10 p-2.5 text-white transition-colors duration-200 hover:bg-white/25"
              aria-label={t("detail.close")}
            >
              <X className="h-5 w-5" />
            </button>
            <img
              src={mediaUrl(photos[lightbox])}
              alt={t("detail.photoAlt", { name: listing.business_name, n: lightbox + 1 })}
              onClick={(e) => e.stopPropagation()}
              data-testid="lightbox-image"
              className="max-h-[62vh] max-w-full rounded-lg object-contain shadow-2xl sm:max-h-[70vh]"
            />
            {photos.length > 1 && (
              <>
                <button
                  type="button"
                  data-testid="lightbox-prev"
                  onClick={(e) => {
                    e.stopPropagation();
                    setLightbox((i) => (i - 1 + photos.length) % photos.length);
                  }}
                  className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-3 text-white transition-colors duration-200 hover:bg-white/25 sm:left-8"
                  aria-label={t("detail.prevPhoto")}
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
                <button
                  type="button"
                  data-testid="lightbox-next"
                  onClick={(e) => {
                    e.stopPropagation();
                    setLightbox((i) => (i + 1) % photos.length);
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-3 text-white transition-colors duration-200 hover:bg-white/25 sm:right-8"
                  aria-label={t("detail.nextPhoto")}
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
                <p
                  data-testid="lightbox-counter"
                  className="absolute bottom-28 left-1/2 -translate-x-1/2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-semibold text-white"
                >
                  {lightbox + 1} / {photos.length}
                </p>
                <div
                  data-testid="lightbox-thumbs"
                  onClick={(e) => e.stopPropagation()}
                  className="absolute inset-x-0 bottom-5 flex justify-start gap-2 overflow-x-auto px-4 sm:justify-center"
                >
                  {photos.map((p, i) => (
                    <button
                      key={p}
                      type="button"
                      data-testid={`lightbox-thumb-${i}`}
                      onClick={() => setLightbox(i)}
                      className={`h-14 w-20 shrink-0 overflow-hidden rounded-md border-2 transition-colors duration-200 ${
                        i === lightbox ? "border-white" : "border-transparent opacity-60 hover:opacity-100"
                      }`}
                      aria-label={t("detail.photoN", { n: i + 1 })}
                    >
                      <img src={mediaUrl(p)} alt="" className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {related.length > 0 && (
          <section className="mt-16">
            <h2 className="font-display text-xl font-bold tracking-tight sm:text-2xl">
              {t("detail.related", { category: catLabel(listing.category), state: listing.state })}
            </h2>
            <div data-testid="related-listings" className="mt-6 grid gap-4 sm:grid-cols-3">
              {related.map((r) => (
                <Link
                  key={r.id}
                  to={href(`/business/${r.slug || r.id}`)}
                  data-testid={`related-link-${r.id}`}
                  className="rounded-lg border border-border bg-card p-5 transition-[transform,box-shadow] duration-200 hover:-translate-y-1 hover:shadow-md"
                >
                  <p className="font-display font-semibold tracking-tight">{r.business_name}</p>
                  <p className="mt-1.5 text-sm text-slate-500">
                    {r.district}, {r.state}
                  </p>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>

      <AffiliateBanner testId="affiliate-banner-detail" />
    </div>
  );
}
